import React, { useEffect, useState } from "react";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import LoadingOverlay from "../Loaders/LoadingOverlay";
import MissingData from "../Shared/MissingData";
import { useTranslation } from "react-i18next";
import StepsNavButtons from "../Buttons/StepsNavButtons";
import { setObject } from "../../utils/StorageHelpers";
import { useHistory } from "react-router-dom";
import LookupsApi from "../../API/lookups.api";
import { isEmpty } from "lodash";
import { mapLookupToSelect } from "../../utils/Helpers";
import FilterDropdown from "../Dropdown/FilterDropdown";
import { getSessionFilters } from "../../utils/FilterHelpers";
import { ROLES } from "../../utils/constants";

const MaterialSelection = () => {
    const { t } = useTranslation();
    const [gridApi, setGridApi] = useState(null);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [materials, setMaterials] = useState([]);
    const [materialsLoading, setMaterialsLoading] = useState(false);
    const [usersLookup, setUsersLookup] = useState([]);
    const [organizationLookup, setOrganizationLookup] = useState([]);
    const [filtersUsed, setFiltersUsed] = useState(false);

    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("material-selection-for-scans-filters")
    );

    const history = useHistory();

    const headers = ["materialName", "technicalName"];

    useEffect(() => {
        const localStorageFilters = getSessionFilters(
            "material-selection-for-scans-filters"
        );

        if (!isEmpty(localStorageFilters)) {
            getMaterialsLookup(localStorageFilters);
            setFilters(localStorageFilters);
            setAppliedFilters(localStorageFilters);
        } else {
            getMaterialsLookup();
        }
        getUsersLookup();
        fetchOrganizations();
    }, []);

    useEffect(() => {
        updateGridDatasource(gridApi);
    }, [materials]);

    const getUsersLookup = async () => {
        const result = await LookupsApi.getUsers();
        setUsersLookup(mapLookupToSelect(result, "email"));
    };

    const fetchOrganizations = async () => {
        const result = await LookupsApi.getOrganizations();
        setOrganizationLookup(mapLookupToSelect(result) || []);
    };

    const filterOptions = [
        {
            label: t("materialName"),
            name: "materialName",
            type: "Input",
            placeholder: t("materialNamePlaceholder"),
        },
        {
            label: t("createdBy"),
            name: "createdByUsers",
            type: "Dropdown",
            options: usersLookup,
            isMulti: true,
            roles: [
                ROLES.admin,
                ROLES.partnerAdmin,
                ROLES.superAdmin,
                ROLES.support,
            ],
        },
        {
            label: t("onBehalfOf"),
            name: "onBehalfOfUsers",
            type: "Dropdown",
            options: usersLookup,
            isMulti: true,
            roles: [
                ROLES.admin,
                ROLES.partnerAdmin,
                ROLES.superAdmin,
                ROLES.support,
            ],
        },
        {
            label: t("affiliate"),
            name: "organizationId",
            type: "Dropdown",
            options: organizationLookup,
            roles: [
                ROLES.admin,
                ROLES.partnerAdmin,
                ROLES.superAdmin,
                ROLES.support,
            ],
        },
        {
            label: t("capturedAt"),
            name: "createdAt",
            type: "Date",
        },
    ];

    const getMaterialsLookup = async (filters) => {
        setMaterialsLoading(true);
        const result = await LookupsApi.getUsedMaterialsInScans(filters);
        setMaterials(
            result.map((material) => ({
                ...material,
                materialName: material.name,
            }))
        );
        setMaterialsLoading(false);
    };

    const updateGridDatasource = (retrievedGridApi) => {
        if (retrievedGridApi) {
            const formattedMaterials = materials.map((material) => ({
                ...material,
                technicalName: material.technicalName || "--",
            }));
            retrievedGridApi.setRowData(formattedMaterials);
        }
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
        updateGridDatasource(params.api);
    };

    const onRowSelected = () => {
        const selectedRows = gridApi.getSelectedRows();
        setSelectedMaterial(selectedRows[0]);
    };

    const isRowSelected = () => {
        return selectedMaterial ? "" : "disabled";
    };

    const onSelectMaterial = () => {
        setObject("scanFilter", {
            material: {
                label: selectedMaterial.name.trim(),
                value: selectedMaterial.id,
            },
            ...(!isEmpty(appliedFilters?.createdByUsers) && {
                createdByUsers: appliedFilters.createdByUsers,
            }),
            ...(!isEmpty(appliedFilters?.onBehalfOfUsers) && {
                onBehalfOf: appliedFilters.onBehalfOfUsers,
            }),
            ...(appliedFilters?.createdAt?.startDate && {
                capturedStartDate: appliedFilters?.createdAt?.startDate,
            }),
            ...(appliedFilters?.createdAt?.endDate && {
                capturedEndDate: appliedFilters?.createdAt?.endDate,
            }),
        });
        history.push("/scans/details");
    };

    const applyFilters = (specificApplyFilters) => {
        getMaterialsLookup(specificApplyFilters);
        setFiltersUsed(true);
    };

    return (
        <>
            <header className="main-content-header flex items-center justify-between">
                <h2 className="h2 whitespace-nowrap">{t("selectMaterial")}</h2>
                <div className="action-header-wrapper flex items-center">
                    <FilterDropdown
                        filterOptions={filterOptions}
                        filters={filters}
                        setFilters={setFilters}
                        applyFilters={applyFilters}
                        component="material-selection-for-scans"
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                        disabled={materialsLoading}
                    />
                </div>
            </header>
            <div className="card-table shadow mb-5">
                <LoadingOverlay active={materialsLoading}>
                    {materials.length > 0 ? (
                        <div className="shadow scan-table table-grid h-half-screen">
                            <div
                                style={{ height: "100%" }}
                                className="ag-theme-alpine text-center"
                            >
                                <AgGridReact
                                    defaultColDef={{
                                        flex: 1,
                                        minWidth: 100,
                                    }}
                                    overlayLoadingTemplate="<span className='ag-overlay-loading-center'>Please wait while your rows are loading</span>"
                                    rowModelType="clientSide"
                                    rowSelection="single"
                                    rowBuffer={0}
                                    onGridReady={onGridReady}
                                    onSelectionChanged={onRowSelected}
                                    applyColumnDefOrder={true}
                                    onRowDoubleClicked={(row) =>
                                        onSelectMaterial(row.data)
                                    }
                                    suppressHorizontalScroll={true}
                                >
                                    {headers.map((header, index) => (
                                        <AgGridColumn
                                            key={index}
                                            headerName={t(header)}
                                            field={header}
                                            minWidth={150}
                                        />
                                    ))}
                                </AgGridReact>
                            </div>
                        </div>
                    ) : (
                        <MissingData
                            isFilterUsed={filtersUsed}
                            entity={t("scans")}
                            includeText={!materialsLoading}
                        />
                    )}
                </LoadingOverlay>
                <div className="mt-4">
                    <StepsNavButtons
                        next={{
                            action: () => onSelectMaterial(selectedMaterial),
                            disabled: isRowSelected(),
                            label: t("next"),
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default MaterialSelection;
